import React, { Suspense } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { describeUnknownError, reportClientError } from '../features/clientErrors';

export function RouteLoading({ fullPage = false }: { fullPage?: boolean }) {
  return (
    <Box
      role="status"
      aria-label="Loading page"
      sx={{ minHeight: fullPage ? '100vh' : 320, display: 'grid', placeItems: 'center' }}
    >
      <CircularProgress />
    </Box>
  );
}

class RouteErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    onLeave: () => void;
    resetKey: string;
  },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(previous: Readonly<{ resetKey: string }>) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setContext('react', { componentStack: info.componentStack ?? '' });
      Sentry.captureException(error);
    });
    void reportClientError({
      source: 'error-boundary',
      ...describeUnknownError(error),
      componentStack: info.componentStack,
    });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Stack role="alert" spacing={2} sx={{ p: 3, alignItems: 'flex-start' }}>
        <Typography variant="h6">This page couldn't load</Typography>
        <Typography>Check your connection and reload the page to try again.</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload page
          </Button>
          <Button onClick={this.props.onLeave}>Return to workspace</Button>
        </Stack>
      </Stack>
    );
  }
}

/** Keep shell controls available; navigation resets failures, reload retries a cached rejected import. */
export function RouteContent({
  children,
  fallbackPath = '/drivers',
  scope = '',
}: {
  children: React.ReactNode;
  fallbackPath?: string;
  scope?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <RouteErrorBoundary
      key={scope}
      resetKey={location.key}
      onLeave={() => navigate(fallbackPath)}
    >
      <Suspense fallback={<RouteLoading />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}
